import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Decimal from "decimal.js";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@cieda/djayma-ui";
import type { Product } from "@/lib/validation/product";

interface BulkPriceModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly products: readonly Product[];
  readonly onConfirm: (mode: "absolute" | "percent", value: number) => void;
  readonly isLoading: boolean;
}

// Use banker's rounding (ROUND_HALF_EVEN) for whole-FCFA amounts to avoid bias.
Decimal.set({ rounding: Decimal.ROUND_HALF_EVEN });

/**
 * Bulk price update modal.
 * ALL math uses decimal.js per CLAUDE.md rules -- never native floating-point
 * for FCFA. Pricing-engine must review this file before merge.
 *
 * Modes:
 *   - absolute : set every selected product's price to a fixed FCFA value
 *   - percent  : apply a percentage delta (positive = increase, negative = discount)
 *
 * Rounding: banker's rounding (ROUND_HALF_EVEN) to whole FCFA.
 */
export function BulkPriceModal({
  open,
  onOpenChange,
  products,
  onConfirm,
  isLoading,
}: BulkPriceModalProps) {
  const { t } = useTranslation("admin");
  const [mode, setMode] = useState<"absolute" | "percent">("absolute");
  const [rawValue, setRawValue] = useState("");

  // Reset state when the modal closes so re-opening starts fresh.
  useEffect(() => {
    if (!open) {
      setMode("absolute");
      setRawValue("");
    }
  }, [open]);

  const value = useMemo(() => {
    try {
      return new Decimal(rawValue || "0");
    } catch {
      return new Decimal(0);
    }
  }, [rawValue]);

  const preview = useMemo(() => {
    return products.slice(0, 10).map((p) => {
      const oldPrice = new Decimal(p.price_fcfa);
      const newPrice =
        mode === "absolute"
          ? value
          : oldPrice.mul(Decimal.add(1, value.div(100)));
      return {
        id: p.id,
        name: p.name,
        oldPrice: oldPrice.toNumber(),
        newPrice: newPrice.toDecimalPlaces(0).toNumber(),
      };
    });
  }, [products, mode, value]);

  // absolute: must be > 0. percent: must be non-zero (can be negative).
  const isValid = mode === "absolute" ? value.gt(0) : !value.eq(0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t("products.bulk.price")} ({products.length} produit(s))
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Tabs
            value={mode}
            onValueChange={(v) => setMode(v as "absolute" | "percent")}
          >
            <TabsList className="w-full">
              <TabsTrigger value="absolute" className="flex-1">
                {t("products.bulk.price_mode_absolute")}
              </TabsTrigger>
              <TabsTrigger value="percent" className="flex-1">
                {t("products.bulk.price_mode_percent")}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="space-y-1">
            <Label htmlFor="bulk-price-value">
              {mode === "absolute" ? "Nouveau prix (FCFA)" : "Variation (%)"}
            </Label>
            <Input
              id="bulk-price-value"
              type="number"
              inputMode="decimal"
              value={rawValue}
              onChange={(e) => setRawValue(e.target.value)}
              placeholder={mode === "absolute" ? "0 FCFA" : "0%"}
              autoComplete="off"
            />
          </div>

          {/* Preview table */}
          {isValid && (
            <div className="max-h-48 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="px-3 py-1.5 text-left font-medium text-stone-600">
                      Produit
                    </th>
                    <th className="px-3 py-1.5 text-right font-medium text-stone-600">
                      {t("products.bulk.price_preview_old")}
                    </th>
                    <th className="px-3 py-1.5 text-right font-medium text-stone-600">
                      {t("products.bulk.price_preview_new")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {preview.map((row) => (
                    <tr key={row.id}>
                      <td className="max-w-[200px] truncate px-3 py-1.5">
                        {row.name}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {row.oldPrice.toLocaleString("fr-SN")} FCFA
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                        {row.newPrice.toLocaleString("fr-SN")} FCFA
                      </td>
                    </tr>
                  ))}
                  {products.length > 10 && (
                    <tr>
                      <td
                        colSpan={3}
                        className="px-3 py-1.5 text-center text-stone-400"
                      >
                        et {products.length - 10} autre(s)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            disabled={!isValid || isLoading}
            onClick={() => onConfirm(mode, value.toNumber())}
          >
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
